import groupBy from "lodash/groupBy";
export type SourceRef = [string, string, string?, string?];
interface Requirement {
  id: string;
  nodeid: string;
  category: string | null;
  subcategory: string | null;
  name: string;
  description: string | null;
  parameters: string | null;
  procedure: string[];
  acceptancecriteria: string[];
  hasattachment?: any[];
  sourcereference?: any;
}

type TreeData = {
  nodeid: string;
  name: string;
  requirements: Requirement[];
}[];

interface Attachment {
  id: string;
  title: string;
  description: string;
  file?: File;
  url?: string; // for existing files
  nodeid?: string; // if needed
}


export const getAttachmentChanges = (original: Attachment[], updated: Attachment[]) => {
    const toAdd: Attachment[] = [];
    const toEdit: Partial<Attachment>[] = [];
    const toDelete: Attachment[] = [];
    // Remote files whose content changed (crop) — re-uploaded in place by fileid.
    const toReplace: (Attachment & { fileid: string })[] = [];

    const originalMap = new Map(original.map((a) => [a.id, a]));
    const updatedIds = new Set(updated.map((a) => a.id));
  
    for (const upd of updated) {

      const orig = originalMap.get(upd.id);
  
      // 1️⃣ Brand new file
      if (!orig && upd.file && !upd.url) {
        toAdd.push(upd);
        continue;
      }
  
      // 2️⃣ Cropped file replacing a remote one — KH updates the existing file
      // (same fileid), so no delete + re-create. Remote rows use fileid as id.
      if (orig?.url && upd.file && !upd.url) {
        toReplace.push({ ...upd, fileid: orig.id, nodeid: orig.nodeid });
        continue;
      }
  
      // 3️⃣ Metadata changed
      if (
        orig &&
        (orig.title !== upd.title || orig.description !== upd.description)
      ) {
        toEdit.push({
          id: upd.id,
          title: upd.title,
          nodeid: upd.nodeid,
          description: upd.description,
        });
      }
    }
  
    // 4️⃣ Deleted attachments
    for (const orig of original) {
      if (!updatedIds.has(orig.id)) {
        toDelete.push(orig);
      }
    }
  
    return { toAdd, toEdit, toDelete, toReplace };
  }
  


export const getTrimmedString = (str: string | null, maxLength: number = 30): string => {
  if (!str) return "";
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
}

/* --------------------------------------------------------------- */
export const getTreeDataByTestPlan = (data: any): TreeData => {
  /* 1️⃣  normalise to an array of “plan wrappers” */
  const plans = Array.isArray(data) ? data : [data];

  /* 2️⃣  flatten → Requirement[]                                       */
  const allRequirements: Requirement[] = plans.flatMap((plan: any) => {
    const wrappedRequirements = [
      ...(plan?.testplan?.hastestrequirement ?? []),
      ...(plan?.testspecification?.hastestrequirement ?? []),
      ...(plan?.testspecification?.hastestplan ?? []).flatMap(
        (testPlanWrap: any) =>
          testPlanWrap?.testplan?.hastestrequirement ?? []
      ),
    ];

    return wrappedRequirements
      .map((wrap: any) => (wrap?.testrequirement ?? wrap) as Requirement)
      .filter(Boolean);
  });

  /* 3️⃣  filter out blank / null subcategories                         */
  const filtered = allRequirements.filter(
    (req) => req.subcategory && req.subcategory.trim() !== ''
  );

  /* 4️⃣  group by subcategory                                          */
  const grouped = groupBy(filtered, (req) => req.subcategory as string);


  /* 5️⃣  build the tree structure                                     */
  const tree: TreeData = Object.entries(grouped).map(
    ([subcategory, requirements],idx) => ({
      nodeid: `${subcategory.toLowerCase().replace(/\s+/g, '-')}-${idx}`,
      name: subcategory,
      requirements: requirements.map((r) => ({
        ...r,                                // keep everything
        parameters: r.parameters ?? '',
        acceptancecriteria: r.acceptancecriteria ?? [],
        hasattachment: r.hasattachment ?? [],
      })),
    })
  );

  return tree;
};

const normalizeRequirementsFromPlan = (data: any): Requirement[] => {
  const plans = Array.isArray(data) ? data : [data];

  // Test requirement data can arrive from either a test plan, a test
  // specification, or test plans nested under a specification. Keep this
  // normalization shared so flat viewers read the same backend shapes.
  return plans.flatMap((plan: any) => {
    const wrappedRequirements = [
      ...(plan?.testplan?.hastestrequirement ?? []),
      ...(plan?.testspecification?.hastestrequirement ?? []),
      ...(plan?.testspecification?.hastestplan ?? []).flatMap(
        (testPlanWrap: any) =>
          testPlanWrap?.testplan?.hastestrequirement ?? []
      ),
    ];

    return wrappedRequirements
      .map((wrap: any) => (wrap?.testrequirement ?? wrap) as Requirement)
      .filter(Boolean)
      .map((r: any) => ({
        ...r,
        parameters: r.parameters ?? "",
        acceptancecriteria: r.acceptancecriteria ?? [],
        hasattachment: r.hasattachment ?? [],
      }));
  });
};

const MISSING_SOURCE_REFERENCE_ORDER = Number.MAX_SAFE_INTEGER;

const getSourceReference = (requirement: Requirement) =>
  (requirement as any).sourcereference ?? (requirement as any).sourceReference;

const getFirstSourceReferenceValue = (value: any) =>
  Array.isArray(value) ? value[0] : value;

const parseSourceReferenceNumber = (value: any) => {
  const firstValue = getFirstSourceReferenceValue(value);

  if (typeof firstValue === "number") return firstValue;

  const match = String(firstValue ?? "").match(/\d+/);
  return match ? Number(match[0]) : MISSING_SOURCE_REFERENCE_ORDER;
};

const getSourceReferencePage = (requirement: Requirement) => {
  // Viewer ordering follows the source document position. Backend data has
  // used both tuple and object shapes, so keep support for both formats.
  const sourceReference = getSourceReference(requirement);
  const pageValue = Array.isArray(sourceReference)
    ? sourceReference[1]
    : sourceReference?.pages ??
      sourceReference?.page ??
      sourceReference?.pageNumber;

  return parseSourceReferenceNumber(pageValue);
};

const getSourceReferenceSection = (requirement: Requirement) => {
  const sourceReference = getSourceReference(requirement);
  const sectionValue = Array.isArray(sourceReference)
    ? sourceReference[2] ?? sourceReference[3]
    : sourceReference?.section ??
      sourceReference?.sections ??
      sourceReference?.chapter;

  return String(getFirstSourceReferenceValue(sectionValue) ?? "").trim();
};

const compareSourceReferenceSections = (left: string, right: string) => {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;

  // Sections such as "2.1.10" should sort after "2.1.3", so compare every
  // numeric/text segment naturally instead of relying on plain string order.
  const leftParts = left.match(/\d+|[a-z]+/gi) ?? [left];
  const rightParts = right.match(/\d+|[a-z]+/gi) ?? [right];
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftPart = leftParts[index];
    const rightPart = rightParts[index];

    if (leftPart === undefined) return -1;
    if (rightPart === undefined) return 1;

    const leftNumber = Number(leftPart);
    const rightNumber = Number(rightPart);
    const bothNumeric = !Number.isNaN(leftNumber) && !Number.isNaN(rightNumber);
    const diff = bothNumeric
      ? leftNumber - rightNumber
      : leftPart.localeCompare(rightPart, undefined, {
          numeric: true,
          sensitivity: "base",
        });

    if (diff !== 0) return diff;
  }

  return 0;
};

export const getFlatRequirementsBySourcePage = (data: any): Requirement[] =>
  // Used by the Test Requirements viewer: show requirements directly, without
  // category grouping. Page is the primary source location; section only
  // decides the order between requirements that belong to the same page.
  normalizeRequirementsFromPlan(data)
    .map((requirement, index) => ({ requirement, index }))
    .sort((a, b) => {
      const pageDiff =
        getSourceReferencePage(a.requirement) -
        getSourceReferencePage(b.requirement);
      if (pageDiff !== 0) return pageDiff;

      const sectionDiff = compareSourceReferenceSections(
        getSourceReferenceSection(a.requirement),
        getSourceReferenceSection(b.requirement)
      );

      return sectionDiff || a.index - b.index;
    })
    .map(({ requirement }) => requirement);

export const getTreeDataByTestPlan_Reports = (data: any, reports: any): TreeData => {
  // 1) normalise plans input
  const plans = Array.isArray(data) ? data : [data];

  // 2) flatten testplan → requirements
  const allRequirements: Requirement[] = plans.flatMap((plan: any) =>
    (plan?.testplan?.hastestrequirement ?? [])
      .map((wrap: any) => wrap?.testrequirement as Requirement)
      .filter(Boolean)
  );

  // 3) build a Set of testrequirement.nodeid referenced by reports
  const reportNodeIds = new Set<string>();
  const reportsArr = Array.isArray(reports) ? reports : [reports];

  reportsArr.forEach((rep: any) => {
    // handle both shapes:
    //   A) { testreport: { hastestrequirementreport: [...] } }
    //   B) { hastestrequirementreport: [...] }
    const list =
      rep?.testreport?.hastestrequirementreport ??
      rep?.hastestrequirementreport ??
      [];

    list.forEach((wrap: any) => {
      const trr = wrap?.testrequirementreport;
      const links = trr?.istestrequirementreportof ?? [];
      links.forEach((lnk: any) => {
        const nodeid = lnk?.testrequirement?.nodeid;
        if (nodeid) reportNodeIds.add(nodeid);
      });
    });
  });

  // 4) keep only plan requirements that exist in reports by nodeid
  const matched = allRequirements.filter((req) => reportNodeIds.has(req?.nodeid));

  // 5) filter out blank / null subcategories
  const filtered = matched.filter(
    (req) => req?.subcategory && String(req.subcategory).trim() !== ""
  );

  // 6) group by subcategory
  const grouped = groupBy(filtered, (req) => String(req.subcategory));

  // 7) build tree
  const tree: TreeData = Object.entries(grouped).map(([subcategory, requirements], idx) => ({
    nodeid: `${subcategory.toLowerCase().replace(/\s+/g, "-")}-${idx}`,
    name: subcategory,
    requirements: requirements.map((r: any) => ({
      ...r,
      parameters: r.parameters ?? "",
      acceptancecriteria: r.acceptancecriteria ?? [],
      hasattachment: r.hasattachment ?? [],
    })),
  }));

  return tree;
};




export default getTreeDataByTestPlan;

// utils/fileExtPresets.ts
export const IMAGE_EXTS = ['png', 'jpg', 'jpeg'];
export const VIDEO_EXTS = [
  'mp4', 'mov', 'mkv', 'avi', 'webm',
];
export const DOC_EXTS = [
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv',
];
export const PDF_EXTS = ['pdf'];

// Normalizes a /testvision/userroles response (array, {roles:[...]},
// {userroles:[...]}, {data:[...]}, or undefined/null/{}) into a plain
// string array. An empty result means "no roles" for callers that gate
// login on the user having at least one role.
export const normalizeUserRoles = (response: any): string[] => {
  const source =
    (Array.isArray(response) && response) ||
    response?.roles ||
    response?.userroles ||
    response?.data ||
    [];

  if (!Array.isArray(source)) return [];

  return source
    .map((item: any) =>
      typeof item === "string" ? item : item?.role || item?.name || ""
    )
    .filter(Boolean);
};


export function parseSourceRef(ref?: SourceRef) {
  if (!ref || !ref[0]) return { attachmentNodeId: "", page: 1 };
  const [attachmentNodeId, pageSpec] = ref;

  // pageSpec can be "56" or "87-88" (take the first number)
  const match = (pageSpec || "").match(/\d+/);
  const page = match ? Math.max(1, Number(match[0])) : 1;

  return { attachmentNodeId, page };
}
